<script lang="ts">
	import { onMount } from 'svelte';
	import type { CourseModel, SessionModel } from '@24hbs/utils';
	// Utils
	import { dateFullMonthFormat, isValidDate, removeMasterCorsoLaurea } from '$lib/utils/formatter';
	import { lessionModeText, lessonModeIcon, lessonModeTooltip } from '$lib/utils/course-utils';
	import { isTodayBetweenTwoDates } from '$lib/utils/date';
	import { TagStore } from '$lib/utils/tag-store';
	// Components
	import { Icon } from '$lib/components/atoms/Icon';
  import { HourDuration } from '$lib/components/atoms/HourDuration';
	import { CourseCardTag } from '$lib/components/atoms/CourseCardTag';
	import { Tooltip, TooltipDesktop } from '$lib/components/atoms/Tooltip';
	import {
		PUBLIC_ENABLE_BORSADISTUDIOFUORISEDE,
		PUBLIC_DISABLE_BORSADISTUDIOFUORISEDE,
		PUBLIC_CODING_ACADEMY,

		PUBLIC_STATIC_ASSETS_BASE_URL

	} from '$env/static/public';
	import { ImageComponent } from '$lib/components/atoms/Image';
	import { useCourseTypeUtils } from '$lib/utils/courses/type-utils';
	import type { ICourseTypeFields } from '$lib/utils/contentful/types';

	export let course: CourseModel;
	export let session: SessionModel;
	export let isBestSeller: boolean = false;

	const tagStoreManager = TagStore.getInstance();
	const tagReadyStore = tagStoreManager.tagReadyStore;
	const tagStore = tagStoreManager.tagStore;
	let myTags: string[] = [];

	let mainSession = course && course.idMainSession ? course.sessions[course.idMainSession] : null;

	let courseType: ICourseTypeFields | null;

	onMount(async () => {
		if (mainSession && mainSession.courseTypeFull) {
			const courseTypeUtils = useCourseTypeUtils(fetch);
			courseType = await courseTypeUtils.getCourseTypeByName(mainSession.courseTypeFull);
		}
		tagStoreManager.initTagStore();
		tagStoreManager.updateTagsForSession(session, true);
		const colorfulTags = TagStore.getColorfulTagForCourse($tagStore, course);
		const textTags = TagStore.getTextualTagForCourse($tagStore, course);
		if (
			isTodayBetweenTwoDates(
				PUBLIC_ENABLE_BORSADISTUDIOFUORISEDE,
				PUBLIC_DISABLE_BORSADISTUDIOFUORISEDE
			) &&
			courseType &&
			['mba-full-time'].includes(courseType?.slug)
		) {
			myTags = ['borsa_di_studio_fuorisede', ...colorfulTags];
		} else {
			myTags = [...colorfulTags, ...textTags];
		}
	});
</script>

<!-- Add course class to enable custom color -->
<section
	class="hero-product"
>
	<div class="hero-grid">
		<div class="hero-bg" class:hasMedia={false} />
		<div class="hero-content">
			<div class="hero-content-block">
				{#if mainSession && (mainSession.credits_cfu || mainSession.credits_cfp)}
					<div class="credits">
						<Icon size="default" icon="my-courses" />
						{#if mainSession.credits_cfu}
							<span class="credits-text">CFU</span>
						{:else if mainSession.credits_cfp}
							<span class="credits-text">CFP</span>
						{/if}
					</div>
				{/if}
				<div class="hero-content-header">
					<p class="hero-course-type">
						{#if mainSession && mainSession.productCode !== PUBLIC_CODING_ACADEMY}
							{course.courseTypeFull.toLocaleLowerCase() === 'master post laurea con stage'
								? `${course.courseTypeFull} garantito`
								: course.courseTypeFull}
						{:else}
							CODING ACADEMY
						{/if}
					</p>
					<h1 class="hero-course-title">
						{course.title}
					</h1>
					<p class="hero-course-subtitle">
						{course.subtitle}
					</p>
				</div>
				{#if myTags.length}
					<CourseCardTag tags={myTags} allTags={true} />
				{/if}
				<div class="hero-content-footer">
					<ul class="course-data">
						{#if session && isValidDate(session.startDate)}
							<li class="course-data-item">
								<span class="data-icon">
									<Icon icon="calendar" />
								</span>
								<span class="data-text">
									{#if session.lessonsAvailableImmediately}
										Lezioni disponibili da subito
									{:else}
										{dateFullMonthFormat(session.startDate)}
									{/if}
								</span>
							</li>
						{/if}
						{#if session && session.duration && session.durationType}
							<li class="course-data-item">
								<span class="data-icon">
									<Icon icon="duration" />
								</span>
								<span class="data-text">
                  {#if session.hourDuration}
                    <HourDuration hourDuration={session.hourDuration} hasDuration={session.duration ? true : false} />
                  {/if}
									{session.duration}
									{session.durationType}
								</span>
							</li>
						{/if}
						{#if session && session.lessionMode}
							<li class="course-data-item full">
								<span class="data-icon">
									<Icon icon={lessonModeIcon(session.lessionMode)} />
								</span>
								<span class="data-text">
									{lessionModeText(session.lessionMode).toUpperCase()}
								</span>
								{#if lessonModeTooltip(session.lessionMode) !== ''}
									<span class="data-icon info">
										<div class="inline-flex lg:hidden">
											<Tooltip
												icon={lessonModeIcon(session.lessionMode)}
												title={lessionModeText(session.lessionMode)}
												message={lessonModeTooltip(session.lessionMode)}
											>
												<Icon icon="info" size="middle" />
											</Tooltip>
										</div>
										<div class="hidden lg:inline-flex">
											<TooltipDesktop
												position="top-right"
												icon={lessonModeIcon(session.lessionMode)}
												title={lessionModeText(session.lessionMode)}
												message={lessonModeTooltip(session.lessionMode)}
											/>
										</div>
									</span>
								{/if}
							</li>
						{/if}
					</ul>
				</div>
			</div>
		</div>
		{#if session && (session.bannerUrl !== PUBLIC_STATIC_ASSETS_BASE_URL || session.previewUrl !== PUBLIC_STATIC_ASSETS_BASE_URL)}
			<div class="hero-media-wrapper">
				<!-- Image Specs -->
				<!-- Max width: 940px -->
				<!-- Ratio: 2:1 (Mobile), Freeform (Desktop) -->
				<!-- {#if session.bannerUrl} -->
				<!-- <img src={handleUrlClientSide(session.previewUrl)} alt={session.title} class="hero-media" /> -->
				{#if session.bannerUrl !== PUBLIC_STATIC_ASSETS_BASE_URL}
					<ImageComponent
						src={session.bannerUrl}
						class="hero-media"
						alt={removeMasterCorsoLaurea(session.title)}
					/>
				{:else}
					<ImageComponent
						src={session.previewUrl}
						class="hero-media"
						alt={removeMasterCorsoLaurea(session.title)}
					/>
				{/if}
				<!-- {:else} -->
				<!-- <video
            autoplay="autoplay"
            playsinline=""
            muted="muted"
            loop="loop"
            preload="none"
            data-src={course.previewUrl}
            class="hero-video"
          >
            <source src={course.previewUrl} />
          </video> -->

				{#if $$slots['cta-play']}
					<div class="hero-action-wrapper">
						<div class="hero-action">
							<slot name="cta-play" />
						</div>
					</div>
				{/if}
			</div>
		{/if}
	</div>
</section>

<style type="text/scss" lang="scss">
	.hero-product {
		--hero-content-min-height: theme('spacing.80'); /* 320px */
		--cta-play-space-around: theme('spacing.28'); /* 112px */
		@apply relative;

		.hero-grid {
			@apply grid grid-cols-default;
			@apply gap-x-grid-default;
			@apply w-full;
			@apply lg:page-margin lg:page-max-width;
		}

		.hero-bg {
			@apply hidden lg:block;
			@apply absolute inset-0;
			@apply bg-white;
		}

		.hero-media-wrapper {
			@apply col-span-full;
			@apply relative;
			@apply lg:col-start-5;
			@apply lg:row-start-1;

			/** Fix aspect ratio plugin */
			--tw-aspect-w: 16;
			--tw-aspect-h: 9;
			padding-bottom: calc(var(--tw-aspect-h) / var(--tw-aspect-w) * 100%);
			> * {
				@apply absolute w-full h-full;
				@apply inset-0;
			}
			@screen lg {
				&::after {
					content: '';
					@apply absolute inset-0;
					@apply bg-gradient-to-r from-white from-[2%] via-transparent via-20% to-transparent;
					@apply pointer-events-none;
				}
				/* Remove aspect ratio */
				padding-bottom: 0;
				margin-right: calc(var(--page-margin) * -1);
			}
		}

		.hero-content-block .credits {
			position: absolute;
			right: 0;
			top: 15px;
			width: 90px;
			@apply flex h-6;
		}
		.hero-content-block .credits-text {
			@apply h-6 ml-2;
		}

		.hero-video {
			@apply absolute inset-0 object-cover w-full h-full;
		}

		.hero-action-wrapper {
			@apply flex items-end justify-start;
			@apply pointer-events-none;
		}

		.hero-action {
			@apply pointer-events-auto;
			@apply flex items-center;
			@apply pl-4 pb-4;
			@apply lg:pl-6 lg:pb-0;

			@screen lg {
				height: var(--cta-play-space-around);
			}
		}

		.hero-course-type {
			@apply uppercase tracking-wide font-semibold;
			@apply text-gray-600 text-sm lg:text-base;
		}

		.hero-course-title {
			@apply text-2xl lg:text-5xl leading-snug;
			@apply text-gray-900 font-medium font-serif;
		}

		.hero-course-subtitle {
			@apply text-black text-sm lg:text-md font-medium text-opacity-60 tracking-wide mt-3 font-serif;
		}

		.hero-course-type + .hero-course-title {
			@apply mt-3;
		}

		.hero-course-title + .hero-course-subtitle {
			@apply mt-3;
		}

		.hero-content {
			@apply relative;
			@apply col-span-full z-1;
			@apply row-start-2;
			@apply lg:mt-18;
			@apply lg:row-start-1 lg:col-start-2 lg:col-span-5;
			@apply lg:self-center;
			@apply shadow;

			@screen lg {
				margin-bottom: var(--cta-play-space-around);
			}
		}

		.hero-content-badge {
			@apply lg:absolute;
			@apply lg:transform lg:-translate-y-full;
		}

		.hero-content-block {
			@apply flex flex-col;
			@apply page-margin py-6;
			@apply bg-white;
			@apply lg:px-6;
			@apply lg:justify-between;
			@apply relative;

			@screen lg {
				min-height: var(--hero-content-min-height);
			}
		}

		.hero-content-footer {
			@apply mt-6;
		}

		.course-data {
			/* Space between list items */
			--space-x: theme('spacing.8'); /* 32px */
			--space-y: theme('spacing.4'); /* 20px */

			@apply flex flex-wrap;
			/* Trick to re-create flex-gap for browsers that don't support it */
			margin-left: calc(var(--space-x) * -1);
			margin-top: calc(var(--space-y) * -1);
		}

		.course-data-item {
			@apply flex items-center;
			@apply uppercase tracking-wide text-xs lg:text-sm text-gray-900;
			margin-left: var(--space-x);
			margin-top: var(--space-y);
		}

		.data-icon {
			@apply inline-flex flex-shrink-0 mr-2;

			&.info {
				@apply flex;
				@apply ml-2;
				@apply text-courses-default;
				@apply cursor-pointer;
			}
		}

		.data-text {
			@apply flex-shrink-0;
		}
	}

	.hero-product :global(.hero-media) {
		@apply absolute w-full h-full;
		@apply inset-0;
		@apply object-cover object-center;
	}
</style>
